import { apiClient, downloadFile, type Paginated } from '@/core/api';
import type {
  AtRiskDto,
  AttendanceBulkInput,
  AttendanceParams,
  AttendanceRegisterDto,
  AttendanceRowDto,
  ExportFormat,
  GradebookDto,
  GradebookParams,
  GradeColumnDto,
  GradeColumnListParams,
  GradeColumnWriteInput,
  ManualGradeDto,
  ManualGradeUpsertInput,
  PupilProgressDto,
} from './types';

// ---------------------------------------------------------------------------
// Read models (computed server-side — none of these are paginated)
// ---------------------------------------------------------------------------

/** `GET /api/gradebook/` — pupils × items matrix. Staff, class-scoped. */
export async function getGradebook(params: GradebookParams): Promise<GradebookDto> {
  const { data } = await apiClient.get<GradebookDto>('/gradebook/', { params });
  return data;
}

/** `GET /api/attendance/` — schedules in the window × enrolled pupils. */
export async function getAttendanceRegister(
  params: AttendanceParams
): Promise<AttendanceRegisterDto> {
  const { data } = await apiClient.get<AttendanceRegisterDto>('/attendance/', { params });
  return data;
}

/** `POST /api/attendance/bulk/` — upsert every mark of one lesson at once. */
export async function bulkMarkAttendance(input: AttendanceBulkInput): Promise<AttendanceRowDto[]> {
  const { data } = await apiClient.post<AttendanceRowDto[]>('/attendance/bulk/', input);
  return data;
}

/**
 * `GET /api/progress/pupils/{member_public_id}/` — readable by the pupil, their
 * parents, a teacher of a class they attend, and admins (the server decides).
 */
export async function getPupilProgress(
  memberPublicId: string,
  schoolClass?: string
): Promise<PupilProgressDto> {
  const { data } = await apiClient.get<PupilProgressDto>(
    `/progress/pupils/${memberPublicId}/`,
    schoolClass ? { params: { school_class: schoolClass } } : undefined
  );
  return data;
}

/** `GET /api/progress/at-risk/?school_class=` — staff only. */
export async function getAtRisk(schoolClass: string): Promise<AtRiskDto> {
  const { data } = await apiClient.get<AtRiskDto>('/progress/at-risk/', {
    params: { school_class: schoolClass },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Manual grade columns
// ---------------------------------------------------------------------------

export async function getGradeColumns(
  params: GradeColumnListParams = {}
): Promise<Paginated<GradeColumnDto>> {
  const { data } = await apiClient.get<Paginated<GradeColumnDto>>('/grade-columns/', { params });
  return data;
}

export async function createGradeColumn(input: GradeColumnWriteInput): Promise<GradeColumnDto> {
  const { data } = await apiClient.post<GradeColumnDto>('/grade-columns/', input);
  return data;
}

export async function updateGradeColumn(
  publicId: string,
  input: GradeColumnWriteInput
): Promise<GradeColumnDto> {
  const { data } = await apiClient.patch<GradeColumnDto>(`/grade-columns/${publicId}/`, input);
  return data;
}

/** `DELETE /api/grade-columns/{id}/` — archives the column (C4); grades stay. */
export async function archiveGradeColumn(publicId: string): Promise<void> {
  await apiClient.delete(`/grade-columns/${publicId}/`);
}

// ---------------------------------------------------------------------------
// Manual grades
// ---------------------------------------------------------------------------

/** `POST /api/manual-grades/` — upsert (201 created / 200 updated). */
export async function upsertManualGrade(input: ManualGradeUpsertInput): Promise<ManualGradeDto> {
  const { data } = await apiClient.post<ManualGradeDto>('/manual-grades/', input);
  return data;
}

// ---------------------------------------------------------------------------
// Exports — staff only; the save goes through the authenticated client so the
// JWT and X-School travel with it (core/api/download.ts).
// ---------------------------------------------------------------------------

export function exportGradebook(params: GradebookParams, format: ExportFormat): Promise<void> {
  return downloadFile('/gradebook/export/', { ...params, format }, `gradebook.${format}`);
}

export function exportAttendance(params: AttendanceParams, format: ExportFormat): Promise<void> {
  return downloadFile('/attendance/export/', { ...params, format }, `attendance.${format}`);
}
